import React, { useEffect, useState } from 'react';
import {
  Container, Grid, Paper, Typography, Box, Avatar, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Button,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EmailIcon from '@mui/icons-material/Email';
import EuroIcon from '@mui/icons-material/Euro';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';

const statusColor = (s) => ({
  pending: 'warning', paid: 'success', shipped: 'info',
  cancelled: 'error', ebay_redirect: 'default', bank_transfer: 'warning',
}[s] || 'default');

const statusLabel = (s) => ({
  pending: 'In attesa', paid: 'Pagato', shipped: 'Spedito',
  cancelled: 'Annullato', ebay_redirect: 'eBay', bank_transfer: 'Bonifico',
}[s] || s);

export default function AdminDashboardPage() {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/orders/admin`, { headers: authHeader() }),
      axios.get(`${API}/inquiries/admin`, { headers: authHeader() }),
    ]).then(([ordersRes, inquiriesRes]) => {
      setOrders(ordersRes.data.orders || []);
      setInquiries(inquiriesRes.data.inquiries || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const totalRevenue = orders.filter(o => o.payment_status === 'paid')
    .reduce((sum, o) => sum + (o.amount - (o.discount_amount || 0)), 0);
  const unreadInquiries = inquiries.filter(i => !i.replied).length;
  const recentOrders = orders.slice(0, 5);

  if (loading) return <Container sx={{ pt: 6, textAlign: 'center' }}><CircularProgress /></Container>;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Dashboard</Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { icon: <ShoppingCartIcon />, label: 'Ordini totali', value: orders.length, color: 'primary.main', action: () => navigate('/admin/ordini') },
          { icon: <EuroIcon />, label: 'Ricavi confermati', value: `€${totalRevenue.toFixed(0)}`, color: 'success.main', action: () => navigate('/admin/ordini') },
          { icon: <EmailIcon />, label: 'Messaggi non letti', value: unreadInquiries, color: 'warning.main', action: () => navigate('/admin/messaggi') },
          { icon: <LocalOfferIcon />, label: 'Gestisci sconti', value: '→', color: 'secondary.main', action: () => navigate('/admin/sconti') },
          { icon: <EuroIcon />, label: 'Prezzi & Sconti catalogo', value: '→', color: 'info.main', action: () => navigate('/admin/prezzi') },
          { icon: <ShoppingCartIcon />, label: 'Proposte d\'acquisto', value: '→', color: 'success.main', action: () => navigate('/admin/proposte') },
          { icon: <EmailIcon />, label: 'Gestisci utenti', value: '→', color: 'error.main', action: () => navigate('/admin/utenti') },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
              onClick={s.action}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: s.color }}>{s.icon}</Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">{s.value}</Typography>
                  <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Ultimi ordini</Typography>
              <Button size="small" onClick={() => navigate('/admin/ordini')}>Vedi tutti</Button>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Articolo</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Importo</TableCell>
                  <TableCell>Metodo</TableCell>
                  <TableCell>Stato</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center">Nessun ordine</TableCell></TableRow>
                )}
                {recentOrders.map(o => (
                  <TableRow key={o.id} hover>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography noWrap variant="body2">{o.item_title}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{o.shipping_email}</Typography></TableCell>
                    <TableCell>€{(o.amount - (o.discount_amount || 0)).toFixed(0)}</TableCell>
                    <TableCell><Typography variant="body2">{o.payment_method}</Typography></TableCell>
                    <TableCell>
                      <Chip label={statusLabel(o.payment_status)} color={statusColor(o.payment_status)} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Messaggi recenti</Typography>
              <Button size="small" onClick={() => navigate('/admin/messaggi')}>Vedi tutti</Button>
            </Box>
            {inquiries.slice(0, 5).map(inq => (
              <Box key={inq.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight="bold">{inq.name || inq.email}</Typography>
                  {!inq.replied && <Chip label="Nuovo" color="warning" size="small" />}
                </Box>
                {inq.item_title && <Typography variant="caption" color="text.secondary">{inq.item_title}</Typography>}
                <Typography variant="body2" color="text.secondary" noWrap>{inq.message}</Typography>
              </Box>
            ))}
            {inquiries.length === 0 && <Typography color="text.secondary">Nessun messaggio</Typography>}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
